import baseURLv1 from '@/services/APIConfig.js';
import { cloneDeep } from 'lodash';
import { add, remove, set, update } from '@/vuex/helpers';

export default {
  namespaced: true,
  state: {
    foreigner: null,
    permits: [],
    PERMIT_STATUS_MISSING_ID: 10,
    questionnaire: null,
    questionnaires: [],
    questionnaireExports: [],
    notifications: [],
    permitDocuments: [],
    permitDocumentsUpdateDraft: [],
    permitStatuses: [],
    permitStatusesUpdateDraft: [],
  },
  mutations: {
    set,
    update,
    add,
    remove,
  },
  actions: {
    async loadForeignerDetail({ commit }, id) {
      const foreigner = await this.$axios.$get(`/api/foreigner/${id}/`);

      foreigner.family = foreigner.family.map((fam) => {
        const profile = foreigner.family_profiles.find((prof) => prof.id === fam.relation_with_id);
        return { ...(profile || {}), ...fam };
      });

      foreigner.family_profiles = [...foreigner.family];

      commit('set', ['foreigner', foreigner]);
    },

    async editForeignerDetail({ commit }, { id, payload }) {
      const foreigner = await this.$axios.$patch(`/api/foreigner/${id}/`, payload);

      foreigner.family = foreigner.family.map((fam) => {
        const profile = foreigner.family_profiles.find((prof) => prof.id === fam.relation_with_id);
        return { ...(profile || {}), ...fam };
      });

      foreigner.family_profiles = [...foreigner.family];

      commit('set', ['foreigner', foreigner]);
    },

    async editForeignerFamily({ commit }, { id, memberId, payload }) {
      const foreigner = await this.$axios.$patch(`/api/foreigner/${id}/family/${memberId}/`, payload);

      foreigner.family = foreigner.family.map((fam) => {
        const profile = foreigner.family_profiles.find((prof) => prof.id === fam.relation_with_id);
        return { ...(profile || {}), ...fam };
      });

      foreigner.family_profiles = [...foreigner.family];

      commit('set', ['foreigner', foreigner]);
    },

    async deleteForeignerFamilyMember({ commit }, { id, memberId }) {
      const foreigner = await this.$axios.$delete(`/api/foreigner/${id}/family/${memberId}/`);

      foreigner.family = foreigner.family.filter((fam) => fam.id !== memberId);
      foreigner.family_profiles =  [...foreigner.family];

      commit('set', ['foreigner', foreigner]);
    },

    async deleteForeigner({ commit }, id) {
      await this.$axios.$delete(`/api/foreigner/${id}/`);

      commit('set', ['foreigner', null]);
    },

    async loadNotifications({ commit }, foreignerId) {
      const data = await this.$axios.$get(`/api/foreigner/${foreignerId}/notification/`);
      commit('set', ['notifications', data]);
    },

    async editNotifications(_, { foreignerId, notifications }) {
      await this.$axios.$patch(`/api/foreigner/${foreignerId}/notification/`, notifications);
    },

    async resetPassword(_, { foreignerId }) {
      return await this.$axios.post(`${baseURLv1}/foreigner/${foreignerId}/reset_password/`, {});
    },

    async uploadAvatar(_, { id, avatar }) {
      const formData = new FormData();

      for (const key in avatar) {
        formData.append(key, avatar[key]);
      }

      const response = await this.$axios.post(`/api/foreigner/${id}/avatar/`, formData, {
        config: {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        },
      });

      return response;
    },

    async loadPermits({ commit }, foreignerId) {
      let permits = await this.$axios.$get(`/api/foreigner/${foreignerId}/permit/`);

      permits = permits.map((permit, index) => {
        permit.color = `permit-${index + 1}`;
        return permit;
      });

      commit('set', ['permits', permits]);
    },

    async addPermit({ commit }, { foreignerId, payload }) {
      const permit = await this.$axios.$post(`/api/foreigner/${foreignerId}/permit/`, payload);

      commit('add', ['permits', permit]);
    },

    async editPermit({ commit }, { permitId, foreignerId, payload }) {
      const permit = await this.$axios.$patch(`/api/foreigner/${foreignerId}/permit/${permitId}/`, payload);

      commit('update', ['permits', {
        id: permit.id,
        payload,
      }]);
    },

    async deletePermit({ commit }, { foreignerId, permitId }) {
      await this.$axios.$delete(`/api/foreigner/${foreignerId}/permit/${permitId}`);

      commit('remove', ['permits', permitId]);
    },

    async loadPermitStatuses({ commit }, { foreignerId, permitId }) {
      const statuses = await this.$axios.$get(`/api/foreigner/${foreignerId}/permit/${permitId}/status`, {
        params: {
          page: 1,
          pageSize: 100,
        },
      });

      commit('set', ['permitStatuses', statuses]);
      commit('set', ['permitStatusesUpdateDraft', cloneDeep(statuses)]);
    },

    async editPermitStatus({ commit }, { permitId, foreignerId, statusId, payload }) {
      const status = await this.$axios.$patch(`/api/foreigner/${foreignerId}/permit/${permitId}/status/${statusId}/`, payload);

      commit('update', ['permitStatuses', {
        id: status.id,
        payload: status,
      }]);

      commit('update', ['permitStatusesUpdateDraft', {
        id: status.id,
        payload: { ...cloneDeep(status), isUpdated: false },
      }]);
    },

    async loadPermitDocuments({ commit }, { foreignerId, permitId }) {
      const documents = await this.$axios.$get(`/api/foreigner/${foreignerId}/permit/${permitId}/document/`, {
        params: {
          page: 1,
          pageSize: 100,
        },
      });

      commit('set', ['permitDocuments', documents]);
      commit('set', ['permitDocumentsUpdateDraft', cloneDeep(documents)]);
    },

    async markPermitAsObtained({ commit }, { permitId, foreignerId, payload }) {
      const formData = new FormData();

      formData.append('issue_date', payload.issue_date);
      formData.append('expire_date', payload.expire_date);

      payload.documents.forEach((document, i) => {
        const suffix = i === 0 ? '' : `_${i + 1}`;
        formData.append('document_name' + suffix, document.name);
        formData.append('document' + suffix, document.file, document.name);
      });

      const permit = await this.$axios.$post(`/api/foreigner/${foreignerId}/permit/${permitId}/achieved/`, formData, {
        config: {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        },
      });

      commit('update', ['permits', {
        id: permit.id,
        payload: permit,
      }]);
    },

    editObtainedPermit({ commit }, { permitId, foreignerId, payload }) {
      return this.$axios.patch(`${baseURLv1}/foreigner/${foreignerId}/permit/${permitId}/`, payload);
    },

    editObtainedPermitDocument({ commit }, { permitId, foreignerId, documentId, documents }) {
      const formData = new FormData();
      const f_id = foreignerId;
      const p_id = permitId;
      const d_id = documentId;

      let i = 1;
      for (const doc of documents) {

        if (i === 1) {
          formData.append(`document_${d_id}`, doc.file, doc.name);
          formData.append(`document_name_${d_id}`, doc.name);
        } else {
          formData.append(`document_${i}_${d_id}`, doc.file, doc.name);
          formData.append(`document_name_${i}_${d_id}`, doc.name);
        }
        i++;
      }

      return this.$axios({
        method: 'patch',
        url: `${baseURLv1}/foreigner/${f_id}/permit/${p_id}/document/`,
        data: formData,
        config: {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        },
      });
    },

    async addPermitDocument({ commit, state }, { permitId, foreignerId, payload }) {
      const documents = await this.$axios.$post(`/api/foreigner/${foreignerId}/permit/${permitId}/document/`, payload);

      const newDocument = documents.find((doc) => !state.permitDocumentsUpdateDraft.find((docDraft) => docDraft.id === doc.id));
      const newDocumentIndex = documents.indexOf(newDocument);
      const updatedPermitDocumentsUpdateDraft = state.permitDocumentsUpdateDraft.toSpliced(newDocumentIndex, 0, cloneDeep(newDocument));

      commit('set', ['permitDocuments', documents]);
      commit('set', ['permitDocumentsUpdateDraft', updatedPermitDocumentsUpdateDraft]);
    },

    async editPermitDocuments({ commit }, { permitId, foreignerId, payload }) {
      const documents = await this.$axios.$patch(`/api/foreigner/${foreignerId}/permit/${permitId}/document/`, payload);

      commit('set', ['permitDocuments', documents]);
      commit('set', ['permitDocumentsUpdateDraft', cloneDeep(documents)]);
    },

    async loadQuestionnaires({ commit }, foreignerId) {
      const questionnaires = await this.$axios.$get(`${baseURLv1}/foreigner/${foreignerId}/survey/`);

      commit('set', ['questionnaires', questionnaires]);
    },

    async loadQuestionnaire({ commit }, { foreignerId, questionnaireId }) {
      const questionnaire = await this.$axios.$get(`${baseURLv1}/foreigner/${foreignerId}/survey/${questionnaireId}/`);
      commit('set', ['questionnaire', questionnaire]);
    },

    async addQuestionnaire({ commit }, { foreignerId, payload }) {
      const questionnaire = await this.$axios.$post(`${baseURLv1}/foreigner/${foreignerId}/survey/`, payload);

      commit('add', ['questionnaires', questionnaire]);
    },

    async updateQuestionnaire({ commit }, { questionnaireId, foreignerId, payload }) {
      const questionnaire = await this.$axios.$patch(`${baseURLv1}/foreigner/${foreignerId}/survey/${questionnaireId}/`, payload);

      commit('set', ['questionnaire', questionnaire]);
      commit('update', ['questionnaires', { id: questionnaire.id, payload: questionnaire }]);
    },

    async deleteQuestionnaire({ commit }, { foreignerId, questionnaireId }) {
      await this.$axios.$delete(`${baseURLv1}/foreigner/${foreignerId}/survey/${questionnaireId}`);

      commit('remove', ['questionnaires', questionnaireId]);
    },

    async loadQuestionnaireExports({ commit }, { foreignerId, questionnaireId }) {
      const questionnaires = await this.$axios.$get(`${baseURLv1}/foreigner/${foreignerId}/survey/${questionnaireId}/export/`);

      commit('set', ['questionnaireExports', questionnaires]);
    },
  },
};
